import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { track } from "@/utils/analytics";
import { useEntitlements } from "../hooks/useEntitlements";
import type { PlanLimitKey } from "../utils";
import { UpgradePaywall } from "./UpgradePaywall";

/** Count limits that have a paywall in the redesign, by their gate id. */
const LIMIT_KEY_BY_GATE = {
	property: "properties",
	amortization: "amortizations_per_mortgage",
	income: "incomes_per_property",
	expense: "expenses_per_property",
	valuation: "valuations_per_property",
} as const satisfies Record<string, PlanLimitKey>;

export type LimitGate = keyof typeof LIMIT_KEY_BY_GATE;

interface LimitPaywallModalProps {
	open: boolean;
	onClose: () => void;
	gate: LimitGate;
}

/**
 * Upgrade paywall for a plan's count limits (properties, amortizations,
 * incomes, expenses, valuations). The limit comes from the current plan.
 *
 * It only opens once the limit is reached, so the meter always reads full at
 * the limit ("2 / 2"): even after a downgrade left the user with more, and
 * when the backend refuses before the local count catches up.
 *
 * Keeps EntitlementGateModal's analytics: same gate ids, paywall_viewed on
 * open, and checkout attributed as `gate_<id>` like the pricing page does.
 */
export function LimitPaywallModal({ open, onClose, gate }: LimitPaywallModalProps) {
	const { t } = useTranslation(["pricing"]);
	const { getLimit } = useEntitlements();
	const limit = getLimit(LIMIT_KEY_BY_GATE[gate]);
	const proName = t("pricing:plans.pro.name");

	// Many screens own their own open state, so "became visible" is the one
	// point common to all of them.
	useEffect(() => {
		if (open) {
			track("paywall_viewed", { gate_id: gate, path: window.location.pathname });
		}
	}, [open, gate]);

	// Gates sit mounted on every list and card; the paywall (plans, checkout,
	// router) only mounts once one actually opens, then stays for its exit
	// animation.
	const [hasOpened, setHasOpened] = useState(open);
	if (open && !hasOpened) setHasOpened(true);
	if (!hasOpened) return null;

	return (
		<UpgradePaywall
			open={open}
			onOpenChange={(next) => !next && onClose()}
			gateId={gate}
			checkoutSource={`gate_${gate}`}
			comparePlansHref={`/pricing?from=${encodeURIComponent(gate)}`}
			title={t(`pricing:paywall.limits.${gate}.title`)}
			body={t(`pricing:paywall.limits.${gate}.body`, { plan: proName })}
			meter={
				limit === null
					? undefined
					: {
							label: t(`pricing:paywall.limits.${gate}.usageLabel`),
							used: limit,
							limit,
						}
			}
		/>
	);
}
